import { api, formatErr } from "./api";
import { colonyApi } from "./colonyApi";

export const ATTACHMENT_MAX_BYTES = 2 * 1024 ** 3;
export const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.docx,.xls,.xlsx,.pptx,.txt,.csv";
export const attachmentSize = size => size >= 1024 ** 3 ? `${(size / 1024 ** 3).toFixed(1)} GB` : size < 1024 ** 2 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 ** 2).toFixed(1)} MB`;
export const attachmentBase = (invoiceId, viewer = "staff") => `/${viewer === "colony" ? "colony" : "mgmt"}/invoices/${encodeURIComponent(invoiceId)}/attachments`;
export const attachmentClient = viewer => viewer === "colony" ? colonyApi : api;
export const attachmentDownloadURL = (invoiceId, fileId, viewer = "staff") => `${process.env.REACT_APP_BACKEND_URL}/api${attachmentBase(invoiceId, viewer)}/${encodeURIComponent(fileId)}/download`;
export const attachmentError = error => error?.response?.data?.detail ? formatErr(error.response.data.detail) : (error?.code === "ERR_CANCELED" ? "Upload cancelled." : error?.message) || "Please try again.";
const pause = ms => new Promise(resolve => setTimeout(resolve, ms));

async function retry(action, signal) {
  for (let attempt = 0; ; attempt += 1) {
    if (signal?.aborted) throw Object.assign(new Error("Upload cancelled."), { code: "ERR_CANCELED" });
    try { return await action(); }
    catch (error) {
      const status = error.response?.status;
      if (attempt >= 3 || (status && status < 500 && ![408, 429].includes(status)) || signal?.aborted || error.code === "ERR_CANCELED") throw error;
      await pause(500 * 2 ** attempt);
    }
  }
}
export { retry as retryAttachmentRequest };

export async function uploadInvoiceAttachment(invoiceId, entry, draftId, signal, onProgress) {
  const base = attachmentBase(invoiceId);
  const file = entry.file;
  const { data: session } = await retry(() => api.post(`${base}/uploads`, { name: file.name, size_bytes: file.size, request_id: entry.id, draft_id: draftId }, { signal, timeout: 30000 }), signal);
  if (session.state === "uploading") {
    for (let index = 0; index < session.total_chunks; index += 1) {
      const chunk = file.slice(index * session.chunk_bytes, (index + 1) * session.chunk_bytes);
      await retry(() => api.put(`${base}/uploads/${session.id}/chunks/${index}`, chunk, {
        signal, timeout: 60000, headers: { "Content-Type": "application/octet-stream" },
        onUploadProgress: event => onProgress({ phase: "uploading", percent: Math.min(99, Math.round((index * session.chunk_bytes + event.loaded) / file.size * 100)) }),
      }), signal);
    }
  }
  onProgress({ phase: "saving", percent: 100 });
  let { data } = await retry(() => api.post(`${base}/uploads/${session.id}/complete`, {}, { signal }), signal);
  while (data.state === "processing" || data.state === "uploading") {
    await pause(1200);
    ({ data } = await retry(() => api.get(`${base}/uploads/${session.id}`, { signal }), signal));
  }
  if (data.state !== "ready") throw new Error(data.error || "The file could not be saved. Please retry.");
  return data.attachment;
}